import { RepoConfigAnalyzerMeta, RepoConfigInterface } from '~/store/repository/detail'

/**
 * Normalize API config format to match component structure
 * Converts flat analyzer config (from API) to nested meta structure (used by components)
 * Example: {name: 'python', enabled: true, runtime_version: '3.x.x'}
 *       -> {name: 'python', enabled: true, type: 'CORE', meta: {runtime_version: '3.x.x'}}
 */
export function normalizeApiConfig(config: any): RepoConfigInterface {
  if (!config.analyzers || !Array.isArray(config.analyzers)) {
    return config
  }

  const normalizedAnalyzers = config.analyzers.map((analyzer: any) => {
    // If already has meta structure, return as-is
    if (analyzer.meta !== undefined) {
      return analyzer
    }

    // Convert flat structure to nested meta structure
    const { name, enabled, type, ...metaFields } = analyzer
    return {
      name,
      enabled: enabled !== undefined ? enabled : true,
      type: type || 'CORE',
      meta: metaFields
    }
  })

  return {
    ...config,
    analyzers: normalizedAnalyzers
  }
}

/**
 * Normalize config for consistent comparison
 * Sorts arrays, normalizes keys, and ensures consistent structure
 */
export function normalizeConfig(config: RepoConfigInterface): RepoConfigInterface {
  const normalizedConfig = JSON.parse(JSON.stringify(config))

  // Ensure arrays exist
  normalizedConfig.analyzers = normalizedConfig.analyzers || []
  normalizedConfig.test_patterns = normalizedConfig.test_patterns || []
  normalizedConfig.exclude_patterns = normalizedConfig.exclude_patterns || []

  if (normalizedConfig.analyzers) {
    normalizedConfig.analyzers.sort((a: any, b: any) => a.name.localeCompare(b.name))
    normalizedConfig.analyzers.forEach((analyzer: any) => {
      // Normalize type to uppercase for consistent comparison
      if (analyzer.type && typeof analyzer.type === 'string') {
        analyzer.type = analyzer.type.toUpperCase()
      }

      if (analyzer.meta) {
        // Sort keys in meta
        const sortedMeta: any = {}
        Object.keys(analyzer.meta)
          .sort()
          .forEach((key) => {
            sortedMeta[key] = analyzer.meta[key]
          })
        analyzer.meta = sortedMeta
      }
    })
  }

  normalizedConfig.test_patterns.sort()
  normalizedConfig.exclude_patterns.sort()

  // Normalize transformers/code_formatters - only keep one key
  if (normalizedConfig.transformers && normalizedConfig.transformers.length > 0) {
    normalizedConfig.transformers.sort((a: any, b: any) => a.name.localeCompare(b.name))
    // Remove code_formatters if transformers exist
    delete normalizedConfig.code_formatters
  } else if (normalizedConfig.code_formatters && normalizedConfig.code_formatters.length > 0) {
    normalizedConfig.code_formatters.sort((a: any, b: any) => a.name.localeCompare(b.name))
    // Remove transformers if code_formatters exist
    delete normalizedConfig.transformers
  } else {
    // Both are empty or undefined, remove both for consistency
    delete normalizedConfig.transformers
    delete normalizedConfig.code_formatters
  }

  return normalizedConfig
}

/**
 * Get the key used for transformers in the config
 * Legacy configs use 'transformers', newer ones use 'code_formatters'
 */
export function getTransformerKey(config: RepoConfigInterface): 'transformers' | 'code_formatters' {
  return config.transformers?.length ? 'transformers' : 'code_formatters'
}

/**
 * Get an analyzer config by name from the config
 */
export function getAnalyzerFromConfig(
  config: RepoConfigInterface,
  name: string
): RepoConfigAnalyzerMeta | Record<string, unknown> {
  const filtered = config.analyzers.filter((analyzer) => analyzer.name === name)
  return filtered.length ? filtered[0] : {}
}

/**
 * Update an analyzer in the config (add, update, or remove)
 * Returns a new config object (does not mutate the original)
 */
export function updateAnalyzerInConfig(
  config: RepoConfigInterface,
  analyzerConfig: RepoConfigAnalyzerMeta
): RepoConfigInterface {
  const newConfig = JSON.parse(JSON.stringify(config)) as RepoConfigInterface

  if (analyzerConfig.enabled) {
    const existingIndex = newConfig.analyzers.findIndex((a) => a.name === analyzerConfig.name)

    if (existingIndex >= 0) {
      // Update existing analyzer
      newConfig.analyzers = newConfig.analyzers.map((a) =>
        a.name === analyzerConfig.name ? analyzerConfig : a
      )
    } else {
      // Add new analyzer at the top
      newConfig.analyzers.unshift(analyzerConfig)
    }
  } else {
    // Remove analyzer
    newConfig.analyzers = newConfig.analyzers.filter((a) => a.name !== analyzerConfig.name)
  }

  return newConfig
}

/**
 * Add a transformer to the config
 * Returns a new config object (does not mutate the original)
 */
export function addTransformerToConfig<T extends { name: string; enabled?: boolean }>(
  config: RepoConfigInterface,
  transformer: T
): RepoConfigInterface {
  const newConfig = JSON.parse(JSON.stringify(config)) as RepoConfigInterface
  const key = getTransformerKey(newConfig)

  const existingTransformers = (newConfig[key] || []) as Array<{ name: string; enabled?: boolean }>

  // Check if already exists
  const exists = existingTransformers.some((t) => t.name === transformer.name)
  if (exists) {
    return newConfig
  }

  // Add at the top with enabled: true
  ;(newConfig as any)[key] = [{ ...transformer, enabled: true }, ...existingTransformers]

  return newConfig
}

/**
 * Remove a transformer from the config by name
 * Returns a new config object (does not mutate the original)
 */
export function removeTransformerFromConfig(
  config: RepoConfigInterface,
  name: string
): RepoConfigInterface {
  const newConfig = JSON.parse(JSON.stringify(config)) as RepoConfigInterface
  const key = getTransformerKey(newConfig)

  const existingTransformers = newConfig[key] || []
  newConfig[key] = existingTransformers.filter((t: any) => t.name !== name)

  return newConfig
}
